import React from 'react';
import { Link } from 'react-router-dom';
import {
  FaArrowRight,
  FaBell,
  FaGamepad,
  FaGooglePlay,
  FaLayerGroup,
  FaMoon,
  FaShieldAlt,
  FaUserSecret
} from 'react-icons/fa';
import SEO, { siteUrl } from '../components/SEO';

const heroImage = '/office-outlaws-hero.png';
const iconImage = '/office-outlaws-icon.png';

const featureCards = [
  {
    icon: FaUserSecret,
    title: 'Hide & Seek Gameplay',
    description: 'Sneak through office spaces, avoid the boss, and survive with smart movement.'
  },
  {
    icon: FaShieldAlt,
    title: 'Boss Attack',
    description: 'Stay out of the spotlight as the boss patrols the office with sharp attention.'
  },
  {
    icon: FaMoon,
    title: 'Day & Night Environments',
    description: 'Play through shifting office moods with different visibility and pressure.'
  },
  {
    icon: FaLayerGroup,
    title: 'Multiple Levels',
    description: 'Progress through office layouts designed for stealth, timing, and quick decisions.'
  }
];

const gameplayPillars = [
  'Outsmart the boss',
  'Find safer routes',
  'Collect rewards',
  'Survive every level'
];

const OfficeOutlaws: React.FC = () => {
  const officeOutlawsJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'VideoGame',
    name: 'Office Outlaws',
    url: `${siteUrl}/office-outlaws`,
    image: `${siteUrl}${heroImage}`,
    gamePlatform: 'Android',
    applicationCategory: 'Game',
    description: 'Office Outlaws is a stealth office hide-and-seek mobile game by RedRender, coming soon on Play Store.'
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-b from-black via-gray-900 to-black pt-24">
      <SEO
        title="Office Outlaws - Coming Soon on Play Store | RedRender"
        description="Office Outlaws is a stealth office hide-and-seek mobile game by RedRender. Hide, sneak, survive, and outsmart the boss. Coming soon on Play Store."
        path="/office-outlaws"
        image={heroImage}
        keywords={[
          'Office Outlaws',
          'Office Outlaws game',
          'Android stealth game',
          'hide and seek mobile game',
          'RedRender game',
          'coming soon Play Store'
        ]}
        jsonLd={officeOutlawsJsonLd}
      />

      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_12%,rgba(255,193,7,0.12),transparent_22rem),radial-gradient(circle_at_78%_18%,rgba(239,68,68,0.18),transparent_28rem),radial-gradient(circle_at_50%_84%,rgba(168,85,247,0.12),transparent_30rem),linear-gradient(rgba(255,255,255,0.012)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.012)_1px,transparent_1px)] bg-[size:auto,auto,auto,72px_72px,72px_72px]" />
      <div className="pointer-events-none absolute left-[-8rem] top-28 h-80 w-80 rounded-full bg-yellow-500/10 blur-3xl" />
      <div className="pointer-events-none absolute right-[-8rem] top-[30rem] h-96 w-96 rounded-full bg-red-600/10 blur-3xl" />

      <section className="relative">
        <div className="container relative z-10 mx-auto px-4 py-16 sm:px-6 sm:py-20 lg:px-8 lg:py-24">
          <div className="grid gap-12 lg:grid-cols-[0.92fr_1.08fr] lg:items-center">
            <div className="animate-slide-up">
              <div className="inline-flex items-center gap-2 rounded-full border border-yellow-400/35 bg-yellow-400/10 px-5 py-2 backdrop-blur-sm">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-yellow-400 opacity-75" />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-yellow-400" />
                </span>
                <span className="font-display text-sm font-semibold text-yellow-200">Coming Soon on Play Store</span>
              </div>

              <h1 className="mt-8 max-w-4xl font-display text-5xl font-bold leading-tight text-white sm:text-6xl lg:text-7xl">
                Office Outlaws
                <span className="mt-2 block bg-gradient-to-r from-yellow-300 via-red-400 to-red-600 bg-clip-text text-transparent">
                  Hide. Sneak. Survive.
                </span>
              </h1>

              <p className="mt-6 max-w-2xl text-lg leading-relaxed text-gray-300 sm:text-xl">
                Outsmart the boss in a playful stealth office adventure built around hide-and-seek gameplay, clever
                routes, multiple levels, and rewarding survival moments.
              </p>

              <div className="mt-10 flex flex-col gap-4 sm:flex-row">
                <div className="inline-flex items-center justify-center gap-3 rounded-full border border-yellow-300/40 bg-gradient-to-r from-yellow-400 via-orange-500 to-red-600 px-8 py-4 font-semibold text-black shadow-[0_0_30px_rgba(251,191,36,0.22)]">
                  <FaGooglePlay />
                  <span>Coming Soon on Play Store</span>
                </div>
                <Link
                  to="/office-outlaws-privacy-policy"
                  className="group inline-flex items-center justify-center gap-3 rounded-full border border-white/15 bg-white/5 px-8 py-4 font-semibold text-white backdrop-blur-sm transition-all duration-300 hover:-translate-y-0.5 hover:border-red-400/50"
                >
                  Privacy Policy
                  <FaArrowRight className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>

              <div className="mt-10 grid max-w-2xl grid-cols-2 gap-4 sm:grid-cols-4">
                {['Stealth', 'Boss Attack', 'Rewards', 'Levels'].map((item) => (
                  <div
                    key={item}
                    className="rounded-2xl border border-white/10 bg-white/5 px-4 py-4 text-center text-sm font-medium text-gray-300 backdrop-blur-sm"
                  >
                    {item}
                  </div>
                ))}
              </div>
            </div>

            <div className="animate-scale-in">
              <div className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-white/[0.04] p-3 shadow-2xl shadow-red-500/10 backdrop-blur-xl">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(250,204,21,0.18),transparent_34%),radial-gradient(circle_at_80%_90%,rgba(239,68,68,0.16),transparent_32%)]" />
                <div className="relative overflow-hidden rounded-[1.5rem] border border-white/10 bg-black">
                  <img
                    src={heroImage}
                    alt="Office Outlaws mobile game banner"
                    className="aspect-[16/9] w-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/10" />
                  <div className="absolute bottom-5 left-5 right-5 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                    <div className="rounded-2xl border border-white/15 bg-black/65 p-4 backdrop-blur-md">
                      <p className="font-display text-xs font-semibold uppercase tracking-[0.28em] text-yellow-200">
                        Mobile stealth game
                      </p>
                      <p className="mt-2 max-w-md text-xl font-bold text-white">Outsmart the boss. Become the ultimate office outlaw.</p>
                    </div>
                    <img
                      src={iconImage}
                      alt="Office Outlaws app icon"
                      className="h-24 w-24 flex-shrink-0 rounded-3xl border border-white/20 shadow-2xl shadow-black/50"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative">
        <div className="container relative z-10 mx-auto px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
          <div className="mb-12 text-center">
            <div className="inline-flex items-center rounded-full border border-red-500/30 bg-red-500/10 px-5 py-2 backdrop-blur-sm">
              <span className="font-display text-sm font-semibold text-red-400">Gameplay Features</span>
            </div>
            <h2 className="mt-6 font-display text-4xl font-bold text-white sm:text-5xl">
              Built around stealth, timing, and <span className="text-red-500">office chaos</span>
            </h2>
          </div>

          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            {featureCards.map((feature, index) => (
              <div
                key={feature.title}
                className="group relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-white/5 to-white/0 p-8 backdrop-blur-xl transition-all duration-500 hover:-translate-y-2 hover:border-yellow-400/40 hover:shadow-2xl hover:shadow-yellow-500/10 animate-scale-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-yellow-500/0 to-red-500/0 opacity-0 transition-opacity duration-500 group-hover:from-yellow-500/10 group-hover:to-red-500/5 group-hover:opacity-100" />
                <feature.icon className="relative text-5xl text-yellow-400 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6" />
                <h3 className="relative mt-6 font-display text-2xl font-bold text-white">{feature.title}</h3>
                <p className="relative mt-3 leading-relaxed text-gray-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative">
        <div className="container relative z-10 mx-auto px-4 py-16 pb-20 sm:px-6 sm:py-20 lg:px-8">
          <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
            <div>
              <div className="inline-flex items-center rounded-full border border-yellow-400/30 bg-yellow-400/10 px-5 py-2 backdrop-blur-sm">
                <span className="font-display text-sm font-semibold text-yellow-200">Game Loop</span>
              </div>
              <h2 className="mt-6 font-display text-4xl font-bold text-white sm:text-5xl">
                Become the ultimate <span className="text-yellow-300">Office Outlaw</span>
              </h2>
              <p className="mt-5 text-lg leading-relaxed text-gray-400">
                Office Outlaws blends simple mobile-friendly controls with stealth pressure, level progression, and
                reward-driven replay value.
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              {gameplayPillars.map((pillar, index) => (
                <div
                  key={pillar}
                  className="rounded-3xl border border-white/10 bg-gradient-to-br from-white/5 to-white/0 p-6 backdrop-blur-xl transition-all duration-500 hover:-translate-y-2 hover:border-red-500/40"
                >
                  <div className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-2xl border border-red-500/30 bg-red-500/10 font-display font-bold text-red-400">
                    0{index + 1}
                  </div>
                  <p className="font-display text-xl font-bold text-white">{pillar}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="relative">
        <div className="container relative z-10 mx-auto px-4 pb-20 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-gradient-to-br from-yellow-500/10 via-white/[0.04] to-red-500/10 px-6 py-12 text-center shadow-2xl shadow-black/20 backdrop-blur-xl sm:px-10 sm:py-16">
            <FaBell className="mx-auto text-5xl text-yellow-300" />
            <h2 className="mt-6 font-display text-3xl font-bold text-white sm:text-4xl lg:text-5xl">
              Coming Soon on Play Store
            </h2>
            <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-gray-300">
              Office Outlaws is being prepared for Android launch. Hide, sneak, survive, and get ready to outsmart the
              boss.
            </p>
            <div className="mt-8 inline-flex items-center gap-3 rounded-full border border-yellow-300/40 bg-yellow-400/10 px-8 py-4 font-semibold text-yellow-100">
              <FaGamepad />
              Play Store launch coming soon
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OfficeOutlaws;
